import { Loading01 } from "@liftedcare/solidjs-untitled-icons/duocolor";
import { useQuery } from "@tanstack/solid-query";
import { Component, createEffect, createSignal, Show } from "solid-js";

import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import { graphql } from "../../schema/index.js";
import { Button } from "../common/Button.js";
import Modal, { ModalBody } from "../modal/Modal.js";
import { useToast } from "../toast/index.js";

const getAgencyExportToken = graphql(`
  query generateSmsExportToken($agencyId: Int!) {
    generateSmsExportToken(agencyId: $agencyId)
  }
`);

export const AgencySmsQuestionnaireExportToken: Component<{
  agencyId?: number | null;
}> = (props) => {
  const agencyId = () => props.agencyId;

  const [timeoutInterval, setTimeoutInterval] = createSignal<null | ReturnType<
    typeof setInterval
  >>(null);
  const [fetchedExportToken, setExportToken] = createSignal<string | null>(
    null,
  );

  const [secondsLeft, setSecondsLeft] = createSignal(0);

  const toast = useToast();

  const timeoutCounter = () => {
    const currentSecondsLeft = secondsLeft();
    if (currentSecondsLeft <= 0) {
      setSecondsLeft(0);
      const currentInterval = timeoutInterval();
      if (currentInterval) {
        clearInterval(currentInterval);
      }
      setTimeoutInterval(null);
      return;
    }
    setSecondsLeft(currentSecondsLeft - 1);
  };

  const exportTokenQuery = useQuery(() => ({
    queryKey: ["exportSMSToken", agencyId()],
    queryFn: async () => {
      const existingAgencyId = agencyId();
      if (!existingAgencyId) {
        return;
      }
      const data = await gqlClient.request(getAgencyExportToken, {
        agencyId: existingAgencyId,
      });
      setExportToken(data.generateSmsExportToken);
      setTimeoutInterval(setInterval(timeoutCounter, 1000));
      setSecondsLeft(5 * 60);
      return data;
    },
    enabled: false,
    staleTime: Number.POSITIVE_INFINITY,
    initialData: null,
  }));

  createEffect(() => {
    if (agencyId()) {
      setExportToken(null);
    }
  });

  const getExportToken = async () => {
    setSecondsLeft(0);
    const currentInterval = timeoutInterval();
    if (currentInterval) {
      clearInterval(currentInterval);
    }
    setTimeoutInterval(null);
    exportTokenQuery.refetch();
  };

  const copyTokenToClipboard = async () => {
    const token = fetchedExportToken();
    if (!token) {
      return;
    }
    await navigator.clipboard.writeText(token);
    toast.success("Success", "Token copied to clipboard");
  };

  return (
    <>
      <Button color="primary" onClick={() => getExportToken()}>
        <Show when={exportTokenQuery.isPending}>
          <Loading01 class="w-4 animate-spin" />
        </Show>
        Generate export token
      </Button>
      <Modal show={!!fetchedExportToken()} onClose={() => setExportToken(null)}>
        <ModalBody>
          <p>Please paste this token into the SMS extension</p>
          <div class="w-full whitespace-pre-wrap break-words rounded-md bg-gray-100 p-4">
            {fetchedExportToken()}
          </div>
          <div class="flex flex-row items-center justify-between">
            Expires in: {`${Math.floor(secondsLeft() / 60)}`.padStart(2, "0")}:
            {`${secondsLeft() % 60}`.padStart(2, "0")}
            <Button class="self-end" onClick={copyTokenToClipboard}>
              Copy token
            </Button>
          </div>
        </ModalBody>
      </Modal>
    </>
  );
};
